import Accordions from "@components/core/accordions/Accordions";
import PageLayout from "@components/core/layouts/PageLayout";
import InfoModal from "@components/core/modals/InfoModal";
import ScellementPictureDefault from "@components/core/preferences/ScellementPictureDefault";
import SignaturePictureDefault from "@components/core/preferences/SignaturePictureDefault";
import CardSection from "@components/core/section/CardSection";
import BasicTabs from "@components/core/tabs/BasicTabs";
import { ROUTES } from "@routes/Routes";
import {
  requestGetOfficePreferences,
  requestUpdateOfficesPreferences,
} from "@state/offices/OfficesEffects";
import { Office, OfficesPreferences } from "@type/core/offices/offices.types";
import { toastError, toastSuccess } from "@utils/helpers/toast-helper";
import useOfficesToDisplay from "@utils/hooks/useOfficesToDisplay";
import AutomaticRelaunchTab from "@views/core/extranet/menu/offices/preferences/AutomaticRelaunchTab";
import { Checkbox } from "antd";
import { FunctionComponent, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

const PreferencesScreen: FunctionComponent = () => {
  const { t } = useTranslation();

  const { officesToDisplay, handleTabClick, activeKey } = useOfficesToDisplay();

  const [preferences, setPreferences] = useState<OfficesPreferences>();

  useEffect(() => {
    void requestGetOfficePreferences({});
  }, []);

  useEffect(() => {
    return requestGetOfficePreferences.done.watch(({ result }) => {
      if (result.ok && result.data) {
        setPreferences(result.data);
      }
    });
  });

  const getDefaultValue = (
    office: Office,
    preferenceKey: string,
    defaultValue?: boolean,
  ): boolean => {
    if (preferences) {
      for (const preference of preferences.officePreferences) {
        if (preference.id === office.id) {
          for (const defaultValue of preference.preferences) {
            if (defaultValue.preferenceKey === preferenceKey) {
              return defaultValue.stringValue === "activated";
            }
          }
        }
      }
    }
    return defaultValue ?? false;
  };

  const checkBoxEvent = (
    office: Office,
    isChecked: boolean,
    preferenceKey: string,
  ) => {
    const stringValue = isChecked ? "activated" : "deactivated";
    void requestUpdateOfficesPreferences({
      dto: {
        preferenceKey: preferenceKey,
        htmlValue: null,
        stringValue: stringValue,
      },
      upperEntityId: office.id,
    })
      .then((result) => {
        if (result.ok && result.data) {
          toastSuccess(t("offices.preferences.errors.checkboxSuccess"));
        }
      })
      .catch(() => {
        toastError(t("offices.preferences.errors.checkboxError"));
      });
  };

  return (
    <PageLayout container returnRoute={ROUTES.extranet.home.generate()}>
      <CardSection title={t("offices.preferences.title")}>
        <BasicTabs
          activeKey={activeKey}
          onTabClick={handleTabClick}
          tabs={officesToDisplay?.map((office) => {
            return {
              title: office.alias,
              content: (
                <Accordions
                  defaultActiveKey={["signaturePictureDefault"]}
                  sections={[
                    {
                      key: "signaturePictureDefault",
                      display: true,
                      title: t(
                        "offices.preferences.signaturePictureDefault.title",
                      ),
                      content: <SignaturePictureDefault office={office} />,
                    },
                    {
                      key: "scellementPicture",
                      display: true,
                      title: t("offices.preferences.scellementPicture.title"),
                      content: <ScellementPictureDefault office={office} />,
                    },
                    {
                      key: "confidentiality",
                      display: office.expertComptable,
                      title: (
                        <>
                          {t("offices.preferences.confidentiality.title")}
                          <InfoModal value="confidentiality" info />
                        </>
                      ),
                      content: (
                        <>
                          <Checkbox
                            defaultChecked={getDefaultValue(
                              office,
                              "isConfidentialityOnlyEditableByCreatorAndEc",
                              true,
                            )}
                            onChange={(e) =>
                              checkBoxEvent(
                                office,
                                e.target.checked,
                                "isConfidentialityOnlyEditableByCreatorAndEc",
                              )
                            }
                          >
                            {t(
                              "offices.preferences.confidentiality.checkbox.isConfidentialityOnlyEditableByCreatorAndEc",
                            )}
                          </Checkbox>
                          <Checkbox
                            defaultChecked={getDefaultValue(
                              office,
                              "isConfidentialByDefault",
                            )}
                            onChange={(e) =>
                              checkBoxEvent(
                                office,
                                e.target.checked,
                                "isConfidentialByDefault",
                              )
                            }
                          >
                            {t(
                              "offices.preferences.confidentiality.checkbox.isConfidentialByDefault",
                            )}
                          </Checkbox>
                        </>
                      ),
                    },
                    {
                      key: "renameDocument",
                      display: true,
                      title: t("offices.preferences.renameDocument.title"),
                      content: (
                        <Checkbox
                          defaultChecked={getDefaultValue(
                            office,
                            "documentRename",
                            true,
                          )}
                          onChange={(e) =>
                            checkBoxEvent(
                              office,
                              e.target.checked,
                              "documentRename",
                            )
                          }
                        >
                          {t(
                            "offices.preferences.renameDocument.checkbox.renameActivated",
                          )}
                        </Checkbox>
                      ),
                    },
                    {
                      key: "automaticRelaunch",
                      display: true,
                      title: t("offices.preferences.automaticRelaunch.title"),
                      content: (
                        <AutomaticRelaunchTab
                          office={office}
                          getDefaultValue={getDefaultValue}
                          checkboxEvent={checkBoxEvent}
                          preferences={preferences}
                        />
                      ),
                    },
                  ]}
                />
              ),
            };
          })}
        />
      </CardSection>
    </PageLayout>
  );
};

export default PreferencesScreen;
